import { useMemo, useState, useEffect } from 'react';

import { useTheme } from '@zenbusiness/zui/styles';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';

import FormStepModal from '#client/components/FormStepModal';
import { COUNTERPARTY_ID, KEY_PARAM_MODAL, MODAL_NAMES } from '#client/constants/modals';
import { FUND_BANK_ACCOUNT } from '#client/constants/nextUpCards';
import {
  WHICH_ACCOUNT_STEP,
  HOW_MUCH_STEP,
  MOVE_MONEY_STEPS_WITHOUT_CHOOSING_TYPE,
  IS_THAT_CORRECT_STEP,
  CONFIRMATION_FORM_STEP
} from '#client/constants/transfer';
import useCheckTransactionContext from '#client/hooks/useCheckTransactionContext';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useDepositContext from '#client/hooks/useDepositContext';
import { FormStepProvider } from '#client/hooks/useFormStep';
import useInitHotJar from '#client/hooks/useHotJar';
import useModalContext from '#client/hooks/useModalContext';
import useNextUpCardsContext from '#client/hooks/useNextUpCardsContext';
import usePlaid from '#client/hooks/usePlaid';
import useUnitAccountId from '#client/hooks/useUnitAccountId';

import CurrentStep from './components/CurrentStep';

export default function DepositMoneyModal({
  steps = MOVE_MONEY_STEPS_WITHOUT_CHOOSING_TYPE,
  handleClose: originHandleClose,
  counterParty = null,
  actionsKit = null
}) {
  // Initialize HotJar to show surveys after deposit
  useInitHotJar();

  const theme = useTheme();
  const { clearAllModal } = useModalContext();
  const { unitAccountId } = useUnitAccountId();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { refetchForFirstTransaction, refetchForFirstPayment } = useCheckTransactionContext();
  const { removeCardWithAnimation } = useNextUpCardsContext();
  const { depositData, depositError } = useDepositContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    amount: 0,
    selectedFromAccount: {},
    fromAccounts: [],
    toAccount: {},
    description: null,
    isResetCounterparties: false,
    initDate: '',
    expectedDate: '',
    hideTitle: false,
    idempotencyKey: null
  });

  const startStep = useMemo(() => {
    if (counterParty) {
      setFormData({
        ...formData,
        selectedFromAccount: counterParty
      });

      return HOW_MUCH_STEP;
    }

    const hasCounterpartyId = searchParams.get(COUNTERPARTY_ID);
    const hasModalname = searchParams.get(KEY_PARAM_MODAL);
    if (hasCounterpartyId && hasModalname === MODAL_NAMES.DEPOSIT_MONEY) {
      return HOW_MUCH_STEP;
    }

    return WHICH_ACCOUNT_STEP;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, counterParty]);

  const [currentFormStep, setCurrentFormStep] = useState(startStep);

  const handleClose = () => {
    if (currentFormStep === CONFIRMATION_FORM_STEP && !depositError && !depositData?.reason) {
      refetchForFirstTransaction();
      refetchForFirstPayment();
      removeCardWithAnimation(FUND_BANK_ACCOUNT);
    }
    originHandleClose();
  };

  const handleBack = () => {
    const currentStepIndex = steps.indexOf(currentFormStep);
    const isClose = (currentFormStep === HOW_MUCH_STEP && !!counterParty) || currentStepIndex <= 0;

    // Using for back on HOW_MUCH_STEP incase has counterpartyId in URL
    const counterpartyId = searchParams.get(COUNTERPARTY_ID);
    if (currentFormStep === HOW_MUCH_STEP && counterpartyId) {
      setSearchParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams);
          newParams.delete(COUNTERPARTY_ID);
          return newParams;
        },
        { replace: true }
      );
    }

    if (isClose) {
      handleClose();
      return;
    }
    setCurrentFormStep(steps[currentStepIndex - 1]);
  };

  const resetSearchParameters = () => {
    if (typeof actionsKit?.resetSearchParameters === 'function') {
      actionsKit.resetSearchParameters();
    }
  };

  const handleCloseModal = () => {
    handleClose();
    clearAllModal();
    resetSearchParameters();
  };

  const closeHandler = () => {
    resetSearchParameters();
    handleClose();
  };

  const onCounterPartyCreatonError = () => {
    setFormData({
      ...formData,
      // True that mean the counter party list should be re-fetch (reset pagination)
      hasError: true
    });
  };

  const onCounterPartyReceived = () => {
    setFormData({
      ...formData,
      isResetCounterparties: true
    });
  };

  const { isHandling } = usePlaid({
    unitAccountId,
    businessEntityUuid,
    counterPartyFields: ['id', 'bank', 'accountNumber', 'status'],
    onCounterPartyReceived,
    onCounterPartyCreatonError
  });

  // back to WHICH_ACCOUNT_STEP
  useEffect(() => {
    const isBackToWhichAccountStep = isHandling && currentFormStep === HOW_MUCH_STEP;
    if (isBackToWhichAccountStep) {
      setCurrentFormStep(WHICH_ACCOUNT_STEP);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHandling]);

  const disableBack = [IS_THAT_CORRECT_STEP, CONFIRMATION_FORM_STEP].includes(currentFormStep);

  return (
    <FormStepModal
      disableBack={disableBack}
      handleBack={handleBack}
      handleClose={handleCloseModal}
      hideTitle={formData.hideTitle}
      title="Deposit Money"
      titleBackground={theme.palette.background.grey.main}
    >
      <FormStepProvider
        value={{
          currentFormStep,
          setCurrentFormStep,
          data: formData,
          setData: setFormData
        }}
      >
        <CurrentStep
          closeHandler={closeHandler}
          currentFormStep={currentFormStep}
          handleBack={handleBack}
          isHandling={isHandling}
        />
      </FormStepProvider>
    </FormStepModal>
  );
}

DepositMoneyModal.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.string),
  handleClose: PropTypes.func.isRequired,
  counterParty: PropTypes.shape({
    id: PropTypes.string,
    bank: PropTypes.string,
    accountNumber: PropTypes.string
  }),
  actionsKit: PropTypes.shape({
    submitDepositSuccessHandler: PropTypes.func,
    resetSearchParameters: PropTypes.func
  })
};
